import { useRef, useState } from 'react';
import { AUTO_SCENE_ID, CUSTOM_SCENE_ID, type BackdropPreset } from '@studio/shared';

import { api, assetUrl } from '../lib/api';
import { downscaleImage } from '../lib/downscale';
import { Carousel } from './Carousel';
import { FilterChips } from './FilterChips';
import { OptionCard } from './OptionCard';
import { PlusIcon, SparkIcon, TrashIcon } from './Icons';
import { useExamples } from '../lib/useExamples';
import { usePresetLabel, useT } from '../lib/i18n';

interface Props {
  scenes: BackdropPreset[];
  selected: string;
  onChange: (id: string) => void;
  custom: string;
  onCustomChange: (text: string) => void;
  backgroundAssetId?: string;
  onBackgroundChange: (assetId: string | undefined) => void;
  /**
   * Several scenes at once: every frame is shot in each. "Leave it to the
   * stylist" stays a choice of its own — picking it clears the others, and
   * picking a scene clears it. Without this the picker chooses one.
   */
  many?: { selected: string[]; onChange: (ids: string[]) => void };
}

/**
 * Where the shoot happens: a studio sweep, one of the locations, or somewhere
 * of your own — described in words, photographed, or both. A background
 * photograph is attached to the renderer and the model is placed inside it.
 */
export function ScenePicker({
  scenes,
  selected,
  onChange,
  custom,
  onCustomChange,
  backgroundAssetId,
  onBackgroundChange,
  many,
}: Props) {
  const tp = usePresetLabel();
  const t = useT();
  const examples = useExamples();
  const [kind, setKind] = useState<'studio' | 'location' | 'paris' | 'all'>('all');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const isOn = (id: string) => (many ? many.selected.includes(id) : selected === id);
  /** Chooses `id`; with several allowed, adds or removes it instead. */
  const pick = (id: string) => {
    if (!many) return onChange(id);
    if (id === AUTO_SCENE_ID) return many.onChange([AUTO_SCENE_ID]);
    const rest = many.selected.filter((s) => s !== AUTO_SCENE_ID);
    const next = rest.includes(id) ? rest.filter((s) => s !== id) : [...rest, id];
    // Nothing chosen is not a place: the last scene cannot be taken away.
    if (next.length) many.onChange(next);
  };
  /** Makes sure `id` is among the chosen, without toggling it off. */
  const ensure = (id: string) => {
    if (!many) return onChange(id);
    if (!many.selected.includes(id)) pick(id);
  };
  const isCustom = isOn(CUSTOM_SCENE_ID);
  // A themed set — Paris — is its own chip; "on location" is everywhere else.
  const themed = (scene: BackdropPreset) => Boolean(scene.theme);
  const visible =
    kind === 'all'
      ? scenes
      : kind === 'paris'
        ? scenes.filter((s) => s.theme === 'paris')
        : scenes.filter((s) => s.kind === kind && !themed(s));

  const upload = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const asset = await api.uploadAsset(await downscaleImage(file), 'backdrop');
      onBackgroundChange(asset.id);
      ensure(CUSTOM_SCENE_ID);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('scene.uploadFailed'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="stack">
      <FilterChips
        groups={[
          {
            id: 'location' as const,
            label: t('scene.onLocation'),
            count: scenes.filter((s) => s.kind === 'location' && !themed(s)).length,
          },
          {
            id: 'paris' as const,
            label: t('scene.paris'),
            count: scenes.filter((s) => s.theme === 'paris').length,
          },
          {
            id: 'studio' as const,
            label: t('scene.studioSweep'),
            count: scenes.filter((s) => s.kind === 'studio').length,
          },
        ]}
        active={kind}
        onChange={setKind}
        total={scenes.length}
      />

      <Carousel count={t('picker.available', { n: visible.length })}>
        {/*
         * Leaving the place to the stylist is a real choice, so it is a card
         * in the row rather than a switch somewhere else: it is chosen the
         * way any scene is chosen, and it reads as one when it is.
         */}
        <OptionCard
          title={t('scene.auto')}
          description={t('scene.autoDesc')}
          spec={t('scene.stylist')}
          selected={isOn(AUTO_SCENE_ID)}
          example={undefined}
          fallback={<SparkIcon />}
          onToggle={() => pick(AUTO_SCENE_ID)}
        />
        {visible.map((scene) => (
          <OptionCard
            key={scene.id}
            title={tp(`scene.${scene.id}.name`, scene.name)}
            description={scene.kind === 'location' ? scene.groundSurface : undefined}
            spec={t(scene.suggestedLightingId ? 'scene.bringsLight' : 'scene.studio')}
            selected={isOn(scene.id)}
            example={examples[`scene-${scene.id}`]}
            swatch={scene.swatch}
            onToggle={() => pick(scene.id)}
          />
        ))}
      </Carousel>

      <div className="stack stack--sm">
        <span className="label">{t('shoot.ownBackground')}</span>

        <div className="rail__track" style={{ scrollSnapType: 'none' }}>
          <div style={{ flex: '0 0 162px' }}>
            <OptionCard
              title={t('scene.custom')}
              description={
                custom.trim() || t(backgroundAssetId ? 'scene.fromPhoto' : 'scene.describeOrUpload')
              }
              selected={isCustom}
              example={undefined}
              fallback={
                backgroundAssetId ? (
                  <img
                    src={assetUrl(backgroundAssetId)}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <PlusIcon />
                )
              }
              onToggle={() => pick(CUSTOM_SCENE_ID)}
            />
          </div>

          <div style={{ flex: 1, minWidth: 260 }}>
            <div className="stack stack--sm">
              <textarea
                className="textarea"
                rows={3}
                value={custom}
                placeholder={t('scene.customPlaceholder')}
                aria-label={t('scene.describe')}
                onChange={(event) => {
                  onCustomChange(event.target.value);
                  if (event.target.value.trim()) ensure(CUSTOM_SCENE_ID);
                }}
              />

              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="sr-only"
                tabIndex={-1}
                aria-hidden="true"
                onChange={(event) => void upload(event.target.files)}
              />

              <div className="row">
                <button
                  type="button"
                  className="btn btn--sm"
                  disabled={uploading}
                  onClick={() => fileRef.current?.click()}
                >
                  {uploading ? <span className="spinner" /> : <PlusIcon />}
                  {t(backgroundAssetId ? 'scene.replacePhoto' : 'scene.uploadPhoto')}
                </button>
                {backgroundAssetId && (
                  <button
                    type="button"
                    className="btn btn--danger btn--sm"
                    aria-label={t('scene.removePhoto')}
                    onClick={() => onBackgroundChange(undefined)}
                  >
                    <TrashIcon />
                  </button>
                )}
              </div>

              {error && <div className="banner banner--error">{error}</div>}
              <p className="faint">{t('scene.photoHint')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
